import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://datexsystems.com';
const COMPANY_NAME = 'DATEX';
const COMPANY_LEGAL_NAME = 'Datex Bilişim AŞ';

// Premium meta tag content - Global Brand: DATEX SYSTEMS
const seoContent = {
  home: {
    en: {
      title: 'DATEX SYSTEMS | AI & Financial Systems Engineering Company',
      description: 'Engineering-driven software company specializing in AI systems, financial platforms, and trading technologies. Building intelligent solutions for enterprise clients.',
      keywords: 'AI systems, financial software, trading systems, fintech development, SaaS development, software engineering company, enterprise solutions, data platforms'
    },
    tr: {
      title: 'DATEX SYSTEMS | AI & Finansal Sistemler Mühendislik Şirketi',
      description: 'AI sistemleri, finansal platformlar ve trading teknolojileri konusunda uzmanlaşmış mühendislik odaklı yazılım şirketi. Kurumsal müşteriler için akıllı çözümler.',
      keywords: 'AI sistemleri, finansal yazılım, trading sistemleri, fintech geliştirme, SaaS geliştirme, yazılım mühendisliği şirketi, kurumsal çözümler, veri platformları'
    },
    de: {
      title: 'DATEX SYSTEMS | KI & Finanzsysteme Engineering-Unternehmen',
      description: 'Engineering-orientiertes Softwareunternehmen spezialisiert auf KI-Systeme, Finanzplattformen und Trading-Technologien. Intelligente Lösungen für Unternehmenskunden.',
      keywords: 'KI-Systeme, Finanzsoftware, Trading-Systeme, Fintech-Entwicklung, SaaS-Entwicklung, Software-Engineering-Unternehmen, Enterprise-Lösungen, Datenplattformen'
    }
  },
  caseStudies: {
    en: {
      title: 'Portfolio & Case Studies | DATEX SYSTEMS',
      description: 'Explore our portfolio of AI-powered financial systems, trading platforms, and enterprise data solutions. Engineering excellence in every project.',
      keywords: 'case studies, portfolio, AI projects, financial systems portfolio, trading platform development, software engineering projects'
    },
    tr: {
      title: 'Portfolyo & Referanslar | DATEX SYSTEMS',
      description: 'AI destekli finansal sistemler, trading platformları ve kurumsal veri çözümlerimizi keşfedin. Her projede mühendislik mükemmelliği.',
      keywords: 'referanslar, portfolyo, AI projeleri, finansal sistem portfolyosu, trading platform geliştirme, yazılım mühendisliği projeleri'
    },
    de: {
      title: 'Portfolio & Referenzen | DATEX SYSTEMS',
      description: 'Entdecken Sie unser Portfolio an KI-gestützten Finanzsystemen, Trading-Plattformen und Enterprise-Datenlösungen. Engineering-Exzellenz in jedem Projekt.',
      keywords: 'Referenzen, Portfolio, KI-Projekte, Finanzsystem-Portfolio, Trading-Plattform-Entwicklung, Software-Engineering-Projekte'
    }
  },
  contact: {
    en: {
      title: 'Contact Us | DATEX SYSTEMS',
      description: 'Start your AI or financial systems project with DATEX SYSTEMS. Get in touch to discuss your requirements and explore partnership opportunities.',
      keywords: 'contact, project inquiry, software development inquiry, AI development services, financial software services'
    },
    tr: {
      title: 'İletişim | DATEX SYSTEMS',
      description: 'DATEX SYSTEMS ile AI veya finansal sistem projenizi başlatın. Gereksinimlerinizi görüşmek ve ortaklık fırsatlarını keşfetmek için iletişime geçin.',
      keywords: 'iletişim, proje talebi, yazılım geliştirme talebi, AI geliştirme hizmetleri, finansal yazılım hizmetleri'
    },
    de: {
      title: 'Kontakt | DATEX SYSTEMS',
      description: 'Starten Sie Ihr KI- oder Finanzsystem-Projekt mit DATEX SYSTEMS. Kontaktieren Sie uns für Ihre Anforderungen und Partnerschaftsmöglichkeiten.',
      keywords: 'Kontakt, Projektanfrage, Softwareentwicklung-Anfrage, KI-Entwicklungsdienstleistungen, Finanzsoftware-Dienstleistungen'
    }
  },
  about: {
    en: {
      title: 'About Us | DATEX - Engineering Excellence',
      description: 'DATEX is an engineering-driven software company focused on building high-complexity AI systems, financial platforms, and data architectures.',
      keywords: 'about DATEX, software engineering company, AI engineering firm, financial technology company, enterprise software development'
    },
    tr: {
      title: 'Hakkımızda | DATEX - Mühendislik Mükemmelliği',
      description: 'DATEX, yüksek karmaşıklıkta AI sistemleri, finansal platformlar ve veri mimarileri kurmaya odaklanmış mühendislik odaklı bir yazılım şirketidir.',
      keywords: 'DATEX hakkında, yazılım mühendisliği şirketi, AI mühendisliği firması, finansal teknoloji şirketi, kurumsal yazılım geliştirme'
    },
    de: {
      title: 'Über Uns | DATEX - Engineering-Exzellenz',
      description: 'DATEX ist ein Engineering-orientiertes Softwareunternehmen, das sich auf hochkomplexe KI-Systeme, Finanzplattformen und Datenarchitekturen konzentriert.',
      keywords: 'über DATEX, Software-Engineering-Unternehmen, KI-Engineering-Firma, Finanztechnologie-Unternehmen, Enterprise-Softwareentwicklung'
    }
  },
  insights: {
    en: {
      title: 'Engineering Insights | DATEX - Notes on Production Systems',
      description: 'Deep technical essays on architecture, infrastructure, and engineering decisions. Thought leadership for CTOs and technical decision-makers.',
      keywords: 'engineering insights, production systems, system architecture, low latency systems, SaaS architecture, financial infrastructure'
    },
    tr: {
      title: 'Mühendislik Notları | DATEX - Production Sistemleri Üzerine',
      description: 'Mimari, altyapı ve mühendislik kararları hakkında derin teknik makaleler. CTO\'lar ve teknik karar vericiler için düşünce liderliği.',
      keywords: 'mühendislik notları, production sistemleri, sistem mimarisi, düşük gecikmeli sistemler, SaaS mimarisi, finansal altyapı'
    },
    de: {
      title: 'Engineering Insights | DATEX - Notizen zu Produktionssystemen',
      description: 'Tiefe technische Essays zu Architektur, Infrastruktur und Engineering-Entscheidungen. Thought Leadership für CTOs und technische Entscheider.',
      keywords: 'Engineering Insights, Produktionssysteme, Systemarchitektur, Low-Latency-Systeme, SaaS-Architektur, Finanzinfrastruktur'
    }
  },
  systems: {
    en: {
      title: 'Systems | DATEX - Production Systems Engineered by Datex',
      description: 'Production-grade software systems designed for reliability, scale, and long-term operational stability. Real-world systems currently in operation.',
      keywords: 'production systems, software systems, system architecture, AI systems, trading infrastructure, B2B platforms, enterprise systems'
    },
    tr: {
      title: 'Sistemler | DATEX - Datex Tarafından Mühendislik Edilen Sistemler',
      description: 'Güvenilirlik, ölçeklenebilirlik ve uzun vadeli operasyonel stabilite için tasarlanmış production-grade yazılım sistemleri.',
      keywords: 'production sistemleri, yazılım sistemleri, sistem mimarisi, AI sistemleri, trading altyapısı, B2B platformları, kurumsal sistemler'
    },
    de: {
      title: 'Systeme | DATEX - Von Datex entwickelte Produktionssysteme',
      description: 'Produktionsreife Softwaresysteme, entwickelt für Zuverlässigkeit, Skalierbarkeit und langfristige operative Stabilität.',
      keywords: 'Produktionssysteme, Softwaresysteme, Systemarchitektur, KI-Systeme, Trading-Infrastruktur, B2B-Plattformen, Enterprise-Systeme'
    }
  }
};

// Project-specific SEO content
const projectSeoContent = {
  borsamex: {
    en: {
      title: 'Borsamex - AI Market Intelligence Platform | DATEX SYSTEMS',
      description: 'Algorithmic market intelligence and predictive signal infrastructure. AI-powered forecasting models for financial decision-making and market analysis.'
    },
    tr: {
      title: 'Borsamex - AI Piyasa İstihbarat Platformu | DATEX SYSTEMS',
      description: 'Algoritmik piyasa istihbaratı ve öngörücü sinyal altyapısı. Finansal karar verme ve piyasa analizi için AI destekli tahmin modelleri.'
    },
    de: {
      title: 'Borsamex - KI-Marktintelligenz-Plattform | DATEX SYSTEMS',
      description: 'Algorithmische Marktintelligenz und prädiktive Signalinfrastruktur. KI-gestützte Prognosemodelle für finanzielle Entscheidungsfindung.'
    }
  },
  datextrader: {
    en: {
      title: 'DatexTrader - Financial Logic & Trading Framework | DATEX SYSTEMS',
      description: 'Modular financial logic and execution framework with risk-aware computation layer. Professional trading and financial application infrastructure.'
    },
    tr: {
      title: 'DatexTrader - Finansal Mantık & Trading Çerçevesi | DATEX SYSTEMS',
      description: 'Risk-farkındalıklı hesaplama katmanına sahip modüler finansal mantık ve yürütme çerçevesi. Profesyonel trading ve finansal uygulama altyapısı.'
    },
    de: {
      title: 'DatexTrader - Finanzlogik & Trading-Framework | DATEX SYSTEMS',
      description: 'Modulares Finanzlogik- und Ausführungsframework mit risikobewusster Berechnungsschicht. Professionelle Trading- und Finanzanwendungsinfrastruktur.'
    }
  },
  izmexcarbon: {
    en: {
      title: 'İzmexCarbon - ESG & Carbon Analytics Platform | DATEX SYSTEMS',
      description: 'ESG intelligence and carbon analytics infrastructure with automated sustainability reporting. Enterprise-grade environmental metrics and compliance engine.'
    },
    tr: {
      title: 'İzmexCarbon - ESG & Karbon Analitik Platformu | DATEX SYSTEMS',
      description: 'Otomatik sürdürülebilirlik raporlaması ile ESG istihbaratı ve karbon analitik altyapısı. Kurumsal düzeyde çevresel metrikler ve uyum motoru.'
    },
    de: {
      title: 'İzmexCarbon - ESG & CO2-Analytik-Plattform | DATEX SYSTEMS',
      description: 'ESG-Intelligence und CO2-Analytik-Infrastruktur mit automatisierter Nachhaltigkeitsberichterstattung. Enterprise-Grade Umweltmetriken und Compliance-Engine.'
    }
  },
  izmexmedia: {
    en: {
      title: 'İzmexMedia - Digital Workflow Platform | DATEX SYSTEMS',
      description: 'Digital media workflow automation and reputation management architecture. Enterprise content pipeline and brand monitoring solutions.'
    },
    tr: {
      title: 'İzmexMedia - Dijital İş Akışı Platformu | DATEX SYSTEMS',
      description: 'Dijital medya iş akışı otomasyonu ve itibar yönetimi mimarisi. Kurumsal içerik pipeline ve marka izleme çözümleri.'
    },
    de: {
      title: 'İzmexMedia - Digitale Workflow-Plattform | DATEX SYSTEMS',
      description: 'Digitale Medien-Workflow-Automatisierung und Reputationsmanagement-Architektur. Enterprise Content-Pipeline und Markenüberwachungslösungen.'
    }
  },
  izmex: {
    en: {
      title: 'İzmex - Industrial B2B Sourcing Platform | DATEX SYSTEMS',
      description: 'Multi-vendor B2B marketplace connecting 12,000+ Turkish manufacturers to 150+ global export markets. Scalable catalog architecture with intelligent search and RFQ workflow automation.'
    },
    tr: {
      title: 'İzmex - Endüstriyel B2B Tedarik Platformu | DATEX SYSTEMS',
      description: '12.000+ Türk üreticiyi 150+ global ihracat pazarına bağlayan çok satıcılı B2B pazar yeri. Akıllı arama ve RFQ iş akışı otomasyonu ile ölçeklenebilir katalog mimarisi.'
    },
    de: {
      title: 'İzmex - Industrielle B2B-Beschaffungsplattform | DATEX SYSTEMS',
      description: 'Multi-Vendor B2B-Marktplatz, der 12.000+ türkische Hersteller mit 150+ globalen Exportmärkten verbindet. Skalierbare Katalogarchitektur mit intelligenter Suche und RFQ-Workflow-Automatisierung.'
    }
  }
};

// Helper to update/create meta tag
function setMetaTag(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

// Helper to update/create link tag
function setLinkTag(rel, href, hreflang = null) {
  let selector = `link[rel="${rel}"]`;
  if (hreflang) selector += `[hreflang="${hreflang}"]`;
  
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    if (hreflang) element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

// Remove existing JSON-LD scripts
function clearJsonLd() {
  document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
}

// Add JSON-LD script
function addJsonLd(data) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

// Generate Organization Schema
function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    'name': COMPANY_NAME,
    'legalName': COMPANY_LEGAL_NAME,
    'url': SITE_URL,
    'logo': `${SITE_URL}/logo.png`,
    'image': `${SITE_URL}/og-image.png`,
    'description': 'Engineering-driven software company specializing in AI systems, financial platforms, and trading technologies.',
    'foundingDate': '2020',
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'Çınar Mah. 5003/2 Sokak No: 3-5, İç Kapı No: 508',
      'addressLocality': 'Bornova',
      'addressRegion': 'İzmir',
      'postalCode': '35030',
      'addressCountry': 'TR'
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'email': 'info@datexsystems.com',
      'availableLanguage': ['English', 'Turkish', 'German']
    },
    'sameAs': [
      'https://linkedin.com/company/datexsystems'
    ],
    'knowsAbout': [
      'Artificial Intelligence',
      'Financial Software Development',
      'Trading Systems',
      'Data Engineering',
      'SaaS Development',
      'Machine Learning'
    ]
  };
}

// Generate WebSite Schema
function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    'name': COMPANY_NAME,
    'url': SITE_URL,
    'publisher': {
      '@id': `${SITE_URL}/#organization`
    },
    'inLanguage': ['en', 'tr', 'de'],
    'potentialAction': {
      '@type': 'SearchAction',
      'target': `${SITE_URL}/case-studies?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
}

// Generate WebPage Schema
function getWebPageSchema(title, description, url, breadcrumbs = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}/#webpage`,
    'url': url,
    'name': title,
    'description': description,
    'isPartOf': {
      '@id': `${SITE_URL}/#website`
    },
    'about': {
      '@id': `${SITE_URL}/#organization`
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url
      }))
    }
  };
}

// Generate Service Schema for homepage
function getServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#service`,
    'name': COMPANY_NAME,
    'url': SITE_URL,
    'provider': {
      '@id': `${SITE_URL}/#organization`
    },
    'serviceType': [
      'AI Systems Development',
      'Financial Software Engineering',
      'Trading Platform Development',
      'SaaS Development',
      'Data Platform Engineering'
    ],
    'areaServed': {
      '@type': 'GeoShape',
      'name': 'Worldwide'
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Software Engineering Services',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'AI & Predictive Systems',
            'description': 'Machine learning models and AI-powered automation systems'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Financial & Trading Technologies',
            'description': 'Trading platforms, risk engines, and financial software'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'SaaS & Platform Engineering',
            'description': 'Scalable SaaS products and enterprise platforms'
          }
        }
      ]
    }
  };
}

export function SEO({ page, projectId = null, articleSlug = null }) {
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Get content based on page and language
    let content;
    if (projectId && projectSeoContent[projectId]) {
      content = projectSeoContent[projectId][currentLanguage] || projectSeoContent[projectId].en;
    } else {
      content = seoContent[page]?.[currentLanguage] || seoContent[page]?.en || seoContent.home.en;
    }

    const title = content?.title || `${COMPANY_NAME} | AI & Financial Systems Engineering`;
    const description = content?.description || 'Engineering-driven software company specializing in AI systems and financial platforms.';
    const keywords = content?.keywords || '';

    // Build canonical URL
    let canonicalPath = '/';
    let breadcrumbs = [{ name: 'Home', url: SITE_URL }];

    if (page === 'caseStudies') {
      canonicalPath = '/case-studies';
      breadcrumbs.push({ name: 'Case Studies', url: `${SITE_URL}/case-studies` });
    } else if (page === 'contact') {
      canonicalPath = '/contact';
      breadcrumbs.push({ name: 'Contact', url: `${SITE_URL}/contact` });
    } else if (page === 'about') {
      canonicalPath = '/about';
      breadcrumbs.push({ name: 'About', url: `${SITE_URL}/about` });
    } else if (page === 'projectDetail' && projectId) {
      canonicalPath = `/case-studies/${projectId}`;
      breadcrumbs.push({ name: 'Case Studies', url: `${SITE_URL}/case-studies` });
      breadcrumbs.push({ name: projectId, url: `${SITE_URL}/case-studies/${projectId}` });
    } else if (page === 'insights') {
      canonicalPath = '/insights';
      breadcrumbs.push({ name: 'Engineering Insights', url: `${SITE_URL}/insights` });
    } else if (page === 'systems') {
      canonicalPath = '/systems';
      breadcrumbs.push({ name: 'Systems', url: `${SITE_URL}/systems` });
    } else if (page === 'articleDetail' && articleSlug) {
      canonicalPath = `/insights/${articleSlug}`;
      breadcrumbs.push({ name: 'Engineering Insights', url: `${SITE_URL}/insights` });
      breadcrumbs.push({ name: articleSlug, url: `${SITE_URL}/insights/${articleSlug}` });
    }

    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const ogImage = `${SITE_URL}/og-image.png`;
    const locale = currentLanguage === 'tr' ? 'tr_TR' : currentLanguage === 'de' ? 'de_DE' : 'en_US';

    // Update document title
    document.title = title;
    
    // Update HTML lang attribute
    document.documentElement.lang = currentLanguage;

    // Primary Meta Tags
    setMetaTag('title', title);
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', COMPANY_NAME);
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('googlebot', 'index, follow');

    // Open Graph / Facebook
    setMetaTag('og:type', page === 'home' ? 'website' : 'article', true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', `${COMPANY_NAME} - AI & Financial Systems Engineering`, true);
    setMetaTag('og:site_name', COMPANY_NAME, true);
    setMetaTag('og:locale', locale, true);

    // Twitter / X
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:url', canonicalUrl);
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:image:alt', `${COMPANY_NAME} - AI & Financial Systems Engineering`);

    // LinkedIn specific
    setMetaTag('linkedin:owner', COMPANY_NAME);

    // Canonical URL
    setLinkTag('canonical', canonicalUrl);

    // Hreflang for multi-language SEO
    setLinkTag('alternate', `${SITE_URL}${canonicalPath}`, 'en');
    setLinkTag('alternate', `${SITE_URL}${canonicalPath}`, 'tr');
    setLinkTag('alternate', `${SITE_URL}${canonicalPath}`, 'de');
    setLinkTag('alternate', `${SITE_URL}${canonicalPath}`, 'x-default');

    // Clear existing JSON-LD and add new structured data
    clearJsonLd();
    
    // Always add Organization schema
    addJsonLd(getOrganizationSchema());
    
    // Always add WebSite schema
    addJsonLd(getWebSiteSchema());
    
    // Add WebPage schema with breadcrumbs
    addJsonLd(getWebPageSchema(title, description, canonicalUrl, breadcrumbs));
    
    // Add Service schema on homepage
    if (page === 'home') {
      addJsonLd(getServiceSchema());
    }

    // Add TechArticle schema for case studies
    if (page === 'projectDetail' && projectId) {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        'headline': title,
        'description': description,
        'author': {
          '@id': `${SITE_URL}/#organization`
        },
        'publisher': {
          '@id': `${SITE_URL}/#organization`
        },
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': canonicalUrl
        },
        'datePublished': '2024-01-01',
        'dateModified': '2026-03-09',
        'image': ogImage,
        'articleSection': 'Case Study',
        'keywords': keywords,
        'proficiencyLevel': 'Expert',
        'dependencies': 'Python, TensorFlow, FastAPI, Kubernetes, PostgreSQL'
      });
    }

    // Add FAQ schema for About page
    if (page === 'about') {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': [
          {
            '@type': 'Question',
            'name': 'What services does DATEX provide?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'DATEX specializes in AI systems development, financial software engineering, trading platform development, and enterprise SaaS solutions.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Where is DATEX located?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'DATEX is headquartered in Bornova, İzmir, Turkey, serving clients worldwide.'
            }
          },
          {
            '@type': 'Question',
            'name': 'What industries does DATEX serve?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'DATEX serves fintech, enterprise software, B2B platforms, ESG/sustainability, and digital media industries with production-grade engineering solutions.'
            }
          }
        ]
      });
    }

    // Add ItemList schema for Systems page
    if (page === 'systems') {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'name': 'Production Systems by DATEX',
        'description': 'Real-world production systems engineered by DATEX',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'item': {
              '@type': 'SoftwareApplication',
              'name': 'Borsamex',
              'description': 'AI-powered trading signal engine with <50ms latency',
              'applicationCategory': 'Financial Software',
              'url': `${SITE_URL}/case-studies/borsamex`
            }
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'item': {
              '@type': 'SoftwareApplication',
              'name': 'DatexTrader',
              'description': 'Algorithmic trading infrastructure with <1ms order routing',
              'applicationCategory': 'Trading Platform',
              'url': `${SITE_URL}/case-studies/datextrader`
            }
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'item': {
              '@type': 'SoftwareApplication',
              'name': 'İzmex',
              'description': 'Industrial B2B sourcing platform connecting 12K+ manufacturers to 150+ markets',
              'applicationCategory': 'B2B Marketplace',
              'url': `${SITE_URL}/case-studies/izmex`
            }
          }
        ]
      });
    }

    // Add TechArticle schema for Engineering Insights articles
    if (page === 'articleDetail' && articleSlug) {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        'headline': title,
        'description': description,
        'author': {
          '@id': `${SITE_URL}/#organization`
        },
        'publisher': {
          '@id': `${SITE_URL}/#organization`
        },
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': canonicalUrl
        },
        'datePublished': '2025-01-01',
        'dateModified': '2026-03-09',
        'image': ogImage,
        'articleSection': 'Engineering Insights',
        'keywords': 'system architecture, production systems, SaaS, financial infrastructure',
        'proficiencyLevel': 'Expert'
      });

      // Add separate BreadcrumbList schema for articles
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': item.name,
          'item': item.url
        }))
      });
    }

  }, [page, projectId, articleSlug, currentLanguage, location]);

  return null;
}

export default SEO;
